import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../api/axios';
import { IApiResponseError, IApiState } from '../../../types/api';
import { RootState } from '../..';

export interface IGetActiveGiftCardProvidersResponse {
  status?: boolean;
  message?: string;
  data?: {
    record?: {
      id?: string;
      name?: string;
      img_url?: string;
      gift_card_category_id?: string;
      priceType?: 'Range' | 'Fixed';
      price?: {
        min_price?: {
          amount?: string | number;
          naira?: string | number;
        };
        max_price?: {
          amount?: string | number;
          naira?: string | number;
        };
        fixed_price?: {
          amount?: string | number;
          naira?: string | number;
        };
      };
      country?: {
        name?: string;
        code?: string;
      };
      created_at?: string;
      updated_at?: string;
    }[];
  };
  code?: number;
  errors?: IApiResponseError[];
}

const initialState: IApiState<IGetActiveGiftCardProvidersResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const getActiveGiftCardProvidersAsync = createAsyncThunk(
  'giftCards/activeGiftCardProviders',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState() as RootState;

      const accessToken = state.auth.value?.data?.token?.accessToken;

      const businessId = localStorage.getItem('businessId');

      const config = {
        headers: {
          Authorization: `Token ${accessToken}`,
          'X-Business-Id': businessId,
        },
      };

      const { data } = await api.get(
        `/gift-card/active-provider-gift-cards`,
        config
      );

      return data;
    } catch (err) {
      const error = err as AxiosError<IApiResponseError>;

      return rejectWithValue(error.response?.data);
    }
  }
);

export const activeGiftCardProvidersGetSlice = createSlice({
  name: 'giftCards/activeGiftCardProviders/get',
  initialState,
  reducers: {
    resetGetActiveGiftCardProviders: (state) => {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getActiveGiftCardProvidersAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(getActiveGiftCardProvidersAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload as IGetActiveGiftCardProvidersResponse;
      })
      .addCase(getActiveGiftCardProvidersAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.value = action.payload as IGetActiveGiftCardProvidersResponse;
        state.error = action.error.message;
      });
  },
});

export const { resetGetActiveGiftCardProviders } =
  activeGiftCardProvidersGetSlice.actions;

export default activeGiftCardProvidersGetSlice.reducer;
